import { useEffect } from "react";
import { getPublicOrigin, getPublicUrl } from "./urls";

const OG_COVER_PATH = "/og-cover.png";

/**
 * useSeo — page-level SEO hook.
 *
 * Sets <title>, <meta name="description">, canonical link, Open Graph,
 * and Twitter Card tags using getPublicUrl() — no env vars, ever.
 *
 * @param {object} opts
 * @param {string} opts.title
 * @param {string} opts.description
 * @param {string} [opts.path]      // Defaults to window.location.pathname
 * @param {string} [opts.type]      // og:type — "website" | "article"
 * @param {string} [opts.ogImagePath] // Defaults to /og-cover.png
 */
export function useSeo({
  title,
  description,
  path,
  type = "website",
  ogImagePath = OG_COVER_PATH,
}) {
  useEffect(() => {
    if (title) document.title = title;
    setMeta("description", description);

    const url = getPublicUrl(path);
    const image = getPublicUrl(ogImagePath);

    setLink("canonical", url);
    setMeta("og:url", url, { property: true });
    setMeta("og:title", title, { property: true });
    setMeta("og:description", description, { property: true });
    setMeta("og:type", type, { property: true });
    setMeta("og:image", image, { property: true });
    setMeta("og:site_name", "AnswerWatch", { property: true });

    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);
    setMeta("twitter:image", image);
  }, [title, description, path, type, ogImagePath]);
}

/**
 * setJsonLd — write/replace a typed JSON-LD <script> in <head>.
 * The data argument must already use getPublicUrl() for any url/@id fields.
 */
export function setJsonLd(id, data) {
  let tag = document.getElementById(id);
  if (tag) tag.remove();
  tag = document.createElement("script");
  tag.id = id;
  tag.setAttribute("type", "application/ld+json");
  tag.textContent = JSON.stringify(data);
  document.head.appendChild(tag);
}

/**
 * Standard Organization schema (use on every public page).
 */
export function organizationSchema() {
  const origin = getPublicOrigin();
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": origin + "/#organization",
    name: "AnswerWatch",
    url: origin + "/",
    logo: getPublicUrl("/og-cover.png"),
  };
}

/**
 * Standard WebSite schema (homepage).
 */
export function websiteSchema() {
  const origin = getPublicOrigin();
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": origin + "/#website",
    name: "AnswerWatch",
    url: origin + "/",
    publisher: { "@id": origin + "/#organization" },
  };
}

/**
 * BreadcrumbList builder.
 * @param {Array<{name:string,path:string}>} crumbs
 */
export function breadcrumbSchema(crumbs) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((c, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: c.name,
      item: getPublicUrl(c.path),
    })),
  };
}

// ---------- low-level DOM helpers ----------
function setMeta(key, content, opts = {}) {
  if (!content) return;
  const isProp = opts.property === true;
  const selector = isProp
    ? `meta[property="${key}"]`
    : `meta[name="${key}"]`;
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    if (isProp) tag.setAttribute("property", key);
    else tag.setAttribute("name", key);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function setLink(rel, href) {
  if (!href) return;
  let tag = document.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}
